import { Injectable, computed, inject, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AuthConfig, OAuthErrorEvent, OAuthEvent, OAuthService } from 'angular-oauth2-oidc';

import { OidcSettings } from '../models/oidc-settings.model';
import { SettingsService } from './settings.service';

type AuthStatus = 'idle' | 'loading' | 'unauthenticated' | 'authenticated' | 'error';
type RolesResponse = {
  upn?: unknown;
  Upn?: unknown;
  roles?: unknown;
  Roles?: unknown;
};

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly oauthService = inject(OAuthService);
  private readonly settingsService = inject(SettingsService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly browser = isPlatformBrowser(this.platformId);
  private initializationPromise?: Promise<void>;

  readonly status = signal<AuthStatus>('idle');
  readonly errorMessage = signal<string | null>(null);
  readonly profile = signal<Record<string, unknown> | null>(null);
  readonly accessToken = signal<string | null>(null);
  readonly roles = signal<string[]>([]);
  readonly rolesUser = signal<string | null>(null);
  readonly rolesLoading = signal(false);
  readonly rolesError = signal<string | null>(null);
  readonly settings = signal<OidcSettings | null>(null);
  readonly isAuthenticated = computed(() => this.status() === 'authenticated');
  readonly displayName = computed(() => {
    const claims = this.profile();
    if (!claims) {
      return null;
    }

    const candidate = claims['name'] ?? claims['preferred_username'] ?? claims['email'] ?? claims['sub'];
    return typeof candidate === 'string' ? candidate : null;
  });

  constructor() {
    this.oauthService.events.subscribe((event) => this.handleAuthEvent(event));
  }

  async initialize(): Promise<void> {
    if (!this.browser) {
      return;
    }

    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.status.set('loading');
    this.errorMessage.set(null);

    this.initializationPromise = (async () => {
      const settings = await firstValueFrom(this.settingsService.getOidcSettings());
      this.settings.set(settings);

      const authConfig: AuthConfig = {
        issuer: settings.issuer,
        clientId: settings.clientId,
        responseType: settings.responseType,
        redirectUri: settings.redirectUri,
        postLogoutRedirectUri: settings.postLogoutRedirectUri,
        scope: settings.scope,
        oidc: true,
        strictDiscoveryDocumentValidation: false,
        clearHashAfterLogin: true,
        showDebugInformation: false,
        requireHttps: true,
        sessionChecksEnabled: false
      };

      this.oauthService.configure(authConfig);
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();
      this.syncState();

      if (!settings.clientId) {
        this.status.set('error');
        this.errorMessage.set('The proxy returned no OpenID client ID. Update GOEvProxy security settings before signing in.');
      }
    })().catch((error: unknown) => {
      this.status.set('error');
      this.errorMessage.set(this.toErrorMessage(error));
      throw error;
    });

    return this.initializationPromise;
  }

  async login(): Promise<void> {
    await this.initialize();

    if (this.isAuthenticated()) {
      return;
    }

    if (this.status() === 'error') {
      return;
    }

    this.oauthService.initCodeFlow();
  }

  logout(): void {
    this.oauthService.logOut();
    this.profile.set(null);
    this.accessToken.set(null);
    this.clearRolesState();
    this.status.set('unauthenticated');
  }

  async loadRoles(): Promise<void> {
    const accessToken = this.accessToken();
    if (!accessToken) {
      this.roles.set([]);
      this.rolesUser.set(null);
      this.rolesError.set('Sign in before requesting roles.');
      return;
    }

    this.rolesLoading.set(true);
    this.rolesError.set(null);

    try {
      const response = await firstValueFrom(
        this.http.get<RolesResponse>(`${this.settingsService.getApiBaseUrl()}/api/v1/auth/getRoles`, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        })
      );

      this.roles.set(this.readRoles(response));
      this.rolesUser.set(this.readUpn(response));
    } catch (error: unknown) {
      this.roles.set([]);
      this.rolesUser.set(null);
      this.rolesError.set(this.toErrorMessage(error));
    } finally {
      this.rolesLoading.set(false);
    }
  }

  private handleAuthEvent(event: OAuthEvent): void {
    if (event instanceof OAuthErrorEvent) {
      this.status.set('error');
      this.errorMessage.set(this.toErrorMessage(event.reason));
      return;
    }

    if (event.type === 'token_received' || event.type === 'logout' || event.type === 'session_terminated') {
      this.syncState();
    }
  }

  private syncState(): void {
    const authenticated = this.oauthService.hasValidAccessToken() || this.oauthService.hasValidIdToken();
    this.profile.set((this.oauthService.getIdentityClaims() as Record<string, unknown> | null) ?? null);
    this.accessToken.set(this.oauthService.getAccessToken() || null);
    this.status.set(authenticated ? 'authenticated' : 'unauthenticated');
    if (!authenticated) {
      this.clearRolesState();
    }

    if (authenticated) {
      this.errorMessage.set(null);
    }
  }

  private clearRolesState(): void {
    this.roles.set([]);
    this.rolesUser.set(null);
    this.rolesLoading.set(false);
    this.rolesError.set(null);
  }

  private toErrorMessage(error: unknown): string {
    const oauthError = this.extractOAuthError(error);
    if (oauthError) {
      return oauthError;
    }

    if (error instanceof HttpErrorResponse) {
      if (error.message) {
        return error.message;
      }

      return `HTTP ${error.status}: ${error.statusText || 'Authentication request failed.'}`;
    }

    if (error instanceof Error) {
      return error.message;
    }

    if (typeof error === 'string') {
      return error;
    }

    return 'Unable to complete the OpenID Connect sign-in flow.';
  }

  private extractOAuthError(error: unknown): string | null {
    const candidates = [
      error,
      this.readProperty(error, 'reason'),
      this.readProperty(error, 'error'),
      this.readProperty(this.readProperty(error, 'error'), 'error'),
      this.readProperty(this.readProperty(error, 'error'), 'reason'),
      this.tryParseJson(this.readProperty(error, 'error')),
      this.tryParseJson(this.readProperty(this.readProperty(error, 'error'), 'error'))
    ];

    for (const candidate of candidates) {
      const description = this.readString(candidate, 'error_description');
      if (description) {
        return this.cleanOAuthDescription(description);
      }

      const message = this.readString(candidate, 'message');
      if (message) {
        return message;
      }
    }

    const status = this.readNumber(error, 'status');
    const url = this.readString(error, 'url') ?? this.readString(this.readProperty(error, 'error'), 'url') ?? '';
    if (status === 0) {
      return 'The browser could not redeem the authorization code. Check whether the redirect URI is registered as a Single-page application in Microsoft Entra and whether the token endpoint allows CORS for this origin.';
    }

    if (status === 400 && url.includes('/oauth2/v2.0/token')) {
      return 'The authorization code was returned to the SPA, but Microsoft Entra rejected the token exchange. The most common causes are: the redirect URI is not registered as a Single-page application, the requested scope is invalid for this client, or the app registration does not allow this public client flow.';
    }

    return null;
  }

  private readProperty(value: unknown, key: string): unknown {
    if (!value || typeof value !== 'object') {
      return null;
    }

    return (value as Record<string, unknown>)[key] ?? null;
  }

  private readString(value: unknown, key: string): string | null {
    const property = this.readProperty(value, key);
    return typeof property === 'string' && property.trim() ? property : null;
  }

  private readNumber(value: unknown, key: string): number | null {
    const property = this.readProperty(value, key);
    return typeof property === 'number' ? property : null;
  }

  private cleanOAuthDescription(description: string): string {
    return description.replace(/\s*Trace ID:.*$/is, '').trim();
  }

  private tryParseJson(value: unknown): unknown {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
      return null;
    }

    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }

  private readUpn(response: RolesResponse): string | null {
    const upn = response.upn ?? response.Upn;
    return typeof upn === 'string' && upn.trim() ? upn : null;
  }

  private readRoles(response: RolesResponse): string[] {
    const roles = response.roles ?? response.Roles;
    if (!Array.isArray(roles)) {
      return [];
    }

    return roles.filter((role): role is string => typeof role === 'string' && role.trim().length > 0);
  }
}